import type { PostHog, Properties, FeatureFlagsCallback } from 'posthog-js';
import { POSTHOG } from '@/constants';
import { isLocal } from "@/helpers/cookie.helper";

const getEnvironmentName = (): string => {
  const envType = import.meta.env.VITE_ENVIRONMENT_TYPE as string;
  switch (envType) {
    case 'dev':
      return 'development';
    case 'test':
      return 'qa';
    case 'saras':
      return 'production';
    default:
      return envType || 'unknown';
  }
};

export const environment = getEnvironmentName();

/**
 * posthog-js is heavy (~181KB raw) and used to sit in the eager entry graph via a
 * static import here. It is now DYNAMICALLY imported on an idle callback (see
 * `initPostHog`), so it no longer blocks first paint.
 *
 * To keep the call sites unchanged, this module exposes a thin `posthog` wrapper
 * with the same surface callers already use (`capture`/`identify`/`register`/
 * `reset`/`group`/`isFeatureEnabled`/`onFeatureFlags`/`__loaded`). Calls made
 * before the real client finishes loading are buffered and flushed in order once
 * init completes — so e.g. the very first `$pageview` is preserved. If PostHog is
 * disabled (no token/host or local dev), the buffer is dropped and calls no-op.
 */
let client: PostHog | null = null;
let disabled = false;

/** Method calls (capture/identify/…) recorded before the real client is ready. */
const preInitQueue: Array<(ph: PostHog) => void> = [];

/** Feature-flag subscriptions registered before the real client is ready. */
interface PendingFlagSub {
  callback: FeatureFlagsCallback;
  cancelled: boolean;
  unsubscribe?: () => void;
}
const pendingFlagSubs: PendingFlagSub[] = [];

const runOrQueue = (fn: (ph: PostHog) => void): void => {
  if (client) {
    fn(client);
    return;
  }
  if (disabled) return;
  preInitQueue.push(fn);
};

/** Replay everything buffered before the client loaded, then go straight-through. */
const flushQueues = (ph: PostHog): void => {
  client = ph;

  for (const fn of preInitQueue) {
    try {
      fn(ph);
    } catch (error) {
      console.error(POSTHOG.MESSAGES.POSTHOG_INIT_ERROR, error);
    }
  }
  preInitQueue.length = 0;

  for (const sub of pendingFlagSubs) {
    if (sub.cancelled) continue;
    sub.unsubscribe = ph.onFeatureFlags(sub.callback);
  }
  pendingFlagSubs.length = 0;
};

const dropQueues = (): void => {
  preInitQueue.length = 0;
  pendingFlagSubs.length = 0;
};

export const posthog = {
  capture(eventName: string, properties?: Properties) {
    runOrQueue((ph) => ph.capture(eventName, properties));
  },
  identify(distinctId?: string, properties?: Properties) {
    runOrQueue((ph) => ph.identify(distinctId, properties));
  },
  register(properties: Properties) {
    runOrQueue((ph) => ph.register(properties));
  },
  reset() {
    runOrQueue((ph) => ph.reset());
  },
  group(groupType: string, groupKey: string, properties?: Properties) {
    runOrQueue((ph) => ph.group(groupType, groupKey, properties));
  },
  /**
   * Synchronous read — returns undefined until the client has loaded flags, which
   * matches posthog-js's own pre-init behavior (callers treat `=== true`).
   */
  isFeatureEnabled(flagKey: string): boolean | undefined {
    return client ? client.isFeatureEnabled(flagKey) : undefined;
  },
  /**
   * Subscribe to flag updates. If the client isn't loaded yet, the subscription is
   * buffered and registered once init completes; the returned unsubscribe works in
   * both cases.
   */
  onFeatureFlags(callback: FeatureFlagsCallback): () => void {
    if (client) {
      const unsubscribe = client.onFeatureFlags(callback);
      return () => unsubscribe?.();
    }
    if (disabled) return () => {};

    const sub: PendingFlagSub = { callback, cancelled: false };
    pendingFlagSubs.push(sub);
    return () => {
      sub.cancelled = true;
      sub.unsubscribe?.();
    };
  },
  /** True once the real client has finished initializing. */
  get __loaded(): boolean {
    return !!client?.__loaded;
  },
};

export const initPostHog = () => {
  const posthogToken = import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN;
  const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

  if (!posthogToken || !posthogHost || isLocal) {
    console.warn(POSTHOG.MESSAGES.POSTHOG_CONFIG_MISSING);
    disabled = true;
    dropQueues();
    return;
  }

  const start = () => {
    // One-time cleanup: remove old PostHog cookies set on the root domain
    // (.sarasanalytics.com) from before cross_subdomain_cookie was disabled.
    try {
      const rootDomain = '.sarasanalytics.com';
      document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim();
        if (name.startsWith('ph_') && name.endsWith('_posthog')) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; domain=${rootDomain}; path=/`;
        }
      });
    } catch {
      // Ignore cleanup errors
    }

    import('posthog-js')
      .then(({ default: ph }) => {
        try {
          ph.init(posthogToken, {
            api_host: posthogHost,
            // Managed reverse proxy: api_host points at our proxy domain, so ui_host
            // must be the real PostHog app URL for toolbar / "view in PostHog" links.
            ui_host: 'https://us.posthog.com',
            person_profiles: 'identified_only',
            capture_pageview: false,
            sanitize_properties: (properties: Properties) => {
              if (properties.$current_url) {
                properties.$current_url = properties.$current_url.split('?')[0];
              }
              return properties;
            },
            capture_pageleave: false,
            autocapture: false,
            // Capture Core Web Vitals (LCP/FCP/CLS/INP) as `$web_vitals` events so
            // field performance can be tracked over releases. This is independent of
            // `autocapture` (DOM events), which stays off. Only collects from here
            // forward — there is no retroactive backfill.
            capture_performance: { web_vitals: true },
            persistence: 'localStorage+cookie',
            cross_subdomain_cookie: false,
          });
          flushQueues(ph);
        } catch (error) {
          console.error(POSTHOG.MESSAGES.POSTHOG_INIT_ERROR, error);
        }
      })
      .catch((error) => {
        console.error(POSTHOG.MESSAGES.POSTHOG_INIT_ERROR, error);
      });
  };

  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(start);
  } else {
    setTimeout(start, 0);
  }
};
